import { useLayoutEffect, useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';

import useFrameStyles from './frameStyles';
import { useTranslations } from 'components/utilities';
import usePaymentSettings from 'components/context/PaymentSettings/usePaymentSettings';
import { pay } from 'store/actions/payment';
import getErrorMessage from 'helpers/getErrorMessage';

const usePaymentForm = () => {
	const style = useFrameStyles();

	const { settings } = usePaymentSettings();

	const { t } = useTranslations();

	const { bookingId } = useParams();

	const history = useHistory();

	const [isInitialized, setIsInitialized] = useState(false);

	const [isLoading, setIsLoading] = useState(false);

	const [errors, setErrors] = useState({});

	const [error, setError] = useState('');

	const [isCardValid, setIsCardValid] = useState(false);

	const [paymentCallbackUrl, setPaymentCallbackUrl] = useState('');

	const [isFormVisible, setIsFormVisible] = useState(true);

	const Frames = window.Frames;

	const handleSubmit = async (ev) => {
		ev.preventDefault();

		setIsLoading(true);

		try {
			await Frames.submitCard();
		} catch (error) {
			setError(error);
			setIsLoading(false);
		}
	};

	useLayoutEffect(() => {
		setIsInitialized(false);

		if (!isFormVisible) {
			return;
		}

		Frames.init({
			publicKey: settings.public_key,
			localization: {
				cardNumberPlaceholder: t(
					'payment.form.card_number_placeholder'
				),
				expiryMonthPlaceholder: t(
					'payment.form.expiry_month_placeholder'
				),
				expiryYearPlaceholder: t(
					'payment.form.expiry_year_placeholder'
				),
				cvvPlaceholder: t('payment.form.cvv_placeholder')
			},
			style
		});

		setIsInitialized(true);
		// eslint-disable-next-line
	}, [isFormVisible]);

	useEffect(() => {
		if (!isInitialized) {
			return;
		}

		const handleFrameValidationChanged = (ev) => {
			const newErrors = {};

			switch (ev.element) {
				case 'card-number': {
					newErrors.cardNumber = ev.isValid
						? []
						: [t('payment.form.enter_valid_card_number')];
					break;
				}
				case 'expiry-date': {
					newErrors.expiryDate = ev.isValid
						? []
						: [t('payment.form.enter_valid_expiry_date')];
					break;
				}
				case 'cvv': {
					newErrors.cvv = ev.isValid
						? []
						: [t('payment.form.enter_valid_cvv_code')];
					break;
				}
				default:
					break;
			}

			setErrors((errors) => ({ ...errors, ...newErrors }));
		};

		const handleCardTokenizationFailed = (ev) => {
			setError(t('errors.unknown'));
			setIsLoading(false);
		};

		const handleCardValidationChanged = () => {
			setIsCardValid(Frames.isCardValid());
		};

		const handleCardTokenized = async (ev) => {
			setIsLoading(true);

			setError('');

			try {
				const { data, status, message } = await pay({
					gateway: 'checkout',
					booking_uuid: bookingId,
					method: 'card',
					method_type: '3ds',
					payload: ev
				});

				if (status === 'success') {
					setPaymentCallbackUrl(data.redirect);
					setIsFormVisible(false);
				} else {
					setPaymentCallbackUrl('');

					setError(message || t('errors.unknown'));

					Frames.enableSubmitForm();
				}
			} catch (error) {
				setPaymentCallbackUrl('');

				setError(getErrorMessage(error));

				Frames.enableSubmitForm();
			}

			setIsLoading(false);
		};

		Frames.addEventHandler(
			Frames.Events.FRAME_VALIDATION_CHANGED,
			handleFrameValidationChanged
		);

		Frames.addEventHandler(
			Frames.Events.CARD_TOKENIZATION_FAILED,
			handleCardTokenizationFailed
		);

		Frames.addEventHandler(
			Frames.Events.CARD_VALIDATION_CHANGED,
			handleCardValidationChanged
		);

		Frames.addEventHandler(
			Frames.Events.CARD_TOKENIZED,
			handleCardTokenized
		);

		return () => {
			Frames.removeEventHandler(
				Frames.Events.FRAME_VALIDATION_CHANGED,
				handleFrameValidationChanged
			);

			Frames.removeEventHandler(
				Frames.Events.CARD_TOKENIZATION_FAILED,
				handleCardTokenizationFailed
			);

			Frames.removeEventHandler(
				Frames.Events.CARD_VALIDATION_CHANGED,
				handleCardValidationChanged
			);

			Frames.removeEventHandler(
				Frames.Events.CARD_TOKENIZED,
				handleCardTokenized
			);
		};
		// eslint-disable-next-line
	}, [isInitialized]);

	useEffect(() => {
		const handleMessage = (ev) => {
			if (ev.data.event !== '3dsFinished') {
				return;
			}

			const data = ev.data.data;

			if (data.status !== 'success') {
				setError(t('errors.unknown'));
				setPaymentCallbackUrl('');
				setIsFormVisible(true);
			} else {
				history.push(`/booking-confirmation/${bookingId}`);
			}
		};

		window.addEventListener('message', handleMessage);

		return () => {
			window.removeEventListener('message', handleMessage);
		};
		// eslint-disable-next-line
	}, []);

	return {
		isLoading,
		isFormVisible,
		error,
		errors,
		paymentCallbackUrl,
		handleSubmit,
		isCardValid
	};
};

export default usePaymentForm;
